import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronDown } from 'react-icons/fi';
import SEOPageLayout from '../components/SEOPageLayout';
import { faqItems, SITE_URL } from '../config/seo-data';

const FAQItem = ({ question, answer, isOpen, onToggle }) => (
  <div className="bg-white border border-black/10 rounded-xl overflow-hidden shadow-sm">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-5 text-left hover:bg-[#faf7f6] transition-colors"
    >
      <span className="text-[#1a1012] font-medium pr-4">{question}</span>
      <FiChevronDown className={`w-5 h-5 text-[#ba1609] shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="px-5 pb-5">
            <p className="text-[#5b4d4f] text-sm leading-relaxed">{answer}</p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map((item) => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <SEOPageLayout
      title="Lasmari Chauffeurs | Corfu Transfer FAQ & Questions"
      description="Frequently asked questions about Corfu airport transfers, port pickups, pricing, and travel tips. Everything you need to know before visiting Corfu."
      keywords="corfu transfer FAQ, corfu taxi questions, is uber in corfu, corfu airport taxi tips, corfu transfer prices"
      canonical={`${SITE_URL}/faq`}
    >
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <section className="relative py-24 lg:py-36 bg-[#f6efed]">
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-4xl lg:text-6xl font-light text-[#1a1012] mb-6">
            Frequently Asked Questions
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-[#5b4d4f] text-lg max-w-3xl mx-auto font-light">
            Everything you need to know about Corfu airport transfers, port pickups, and getting around the island.
          </motion.p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-3">
            {faqItems.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <FAQItem
                  question={item.question}
                  answer={item.answer}
                  isOpen={openIndex === i}
                  onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </SEOPageLayout>
  );
};

export default FAQ;
